import { toast } from 'react-hot-toast';

export interface FileDownloadOptions {
  fileName: string;
  mimeType?: string;
  content?: string | Blob;
  url?: string;
}

class FileService {
  async downloadFile({ fileName, mimeType, content, url }: FileDownloadOptions): Promise<void> {
    try {
      let blob: Blob;

      if (url) {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to download file: ${response.statusText}`);
        }
        blob = await response.blob();
      } else if (content instanceof Blob) {
        blob = content;
      } else if (typeof content === 'string') {
        blob = new Blob([content], { type: mimeType || this.getMimeType(fileName) });
      } else {
        throw new Error('No valid content provided for download');
      }

      // Create download URL
      const downloadUrl = window.URL.createObjectURL(blob);

      try {
        // Create and trigger download
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } finally {
        // Always clean up the URL object
        window.URL.revokeObjectURL(downloadUrl);
      }
    } catch (error) {
      console.error('Download failed:', error);
      throw error;
    }
  }

  async simulateFileDownload(fileName: string): Promise<void> {
    try {
      // Generate mock content based on file type
      const extension = this.getFileExtension(fileName).toLowerCase();
      let content: string;

      switch (extension) {
        case 'docx':
          content = `Sample content for ${fileName}\nDocument created: ${new Date().toISOString()}`;
          break;
        case 'xlsx':
          content = `ID,Name,Date\n1,${fileName},${new Date().toISOString()}`;
          break;
        case 'pdf':
          content = `Sample PDF content for ${fileName}\nGenerated: ${new Date().toISOString()}`;
          break;
        default:
          content = `Sample content for ${fileName}\nCreated: ${new Date().toISOString()}`;
      }

      // Download the file
      await this.downloadFile({
        fileName,
        content,
        mimeType: this.getMimeType(fileName)
      });

      toast.success(`Downloaded ${fileName}`);
    } catch (error) {
      console.error('Failed to download file:', error);
      toast.error(`Failed to download ${fileName}`);
      throw error;
    }
  }

  private getFileExtension(fileName: string): string {
    const parts = fileName.split('.');
    return parts.length > 1 ? parts.pop() || '' : '';
  }

  private getMimeType(fileName: string): string {
    const extension = this.getFileExtension(fileName).toLowerCase();
    const mimeTypes: Record<string, string> = {
      'pdf': 'application/pdf',
      'doc': 'application/msword',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'xls': 'application/vnd.ms-excel',
      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'txt': 'text/plain',
      'json': 'application/json',
      'csv': 'text/csv',
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'png': 'image/png',
      'gif': 'image/gif'
    };
    return mimeTypes[extension] || 'application/octet-stream';
  }
}

export const fileService = new FileService();